/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import '../PaymentMethodModal.css';

const PaymentMethodModal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'default',
  onExpand,
  methodKey,
  showFooter = false,
  isEditMode = false,
  alwaysEditable = false,
  saving = false,
  onEdit,
  onSave,
  onCancel,
  textObj = {}
}) => {
  const t = (key, fallback) => textObj[key] || fallback;

  // Handle ESC key to close
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div className="PaymentMethodModal-overlay" onClick={onClose}>
      <div
        className={`PaymentMethodModal-container ${size}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="PaymentMethodModal-header">
          <h2 className="PaymentMethodModal-title">{title}</h2>
          <div className="PaymentMethodModal-headerActions">
            {onExpand && methodKey && (
              <button
                className="PaymentMethodModal-expandBtn"
                onClick={() => onExpand(methodKey)}
                aria-label={t("close", "Close")}
                title={t("close", "Close")}
              >
                <span className="PaymentMethodModal-expandIcon"></span>
              </button>
            )}
            <button
              className="PaymentMethodModal-closeBtn"
              onClick={onClose}
              aria-label={t("close", "Close")}
            >
              &times;
            </button>
          </div>
        </div>

        <div className="PaymentMethodModal-content">
          {children}
        </div>

        {showFooter && (
          <div className="PaymentMethodModal-footer">
            {alwaysEditable ? (
              <button
                className="PaymentMethodModal-saveBtn"
                onClick={onSave}
                disabled={saving}
              >
                {saving ? t("saving", "Saving...") : t("save", "Save")}
              </button>
            ) : isEditMode ? (
              <>
                <button
                  className="PaymentMethodModal-cancelBtn"
                  onClick={onCancel}
                  disabled={saving}
                >
                  {t("cancel", "Cancel")}
                </button>
                <button
                  className="PaymentMethodModal-saveBtn"
                  onClick={onSave}
                  disabled={saving}
                >
                  {saving ? t("saving", "Saving...") : t("save", "Save")}
                </button>
              </>
            ) : (
              <button
                className="PaymentMethodModal-saveBtn"
                onClick={onEdit}
              >
                {t("edit", "Edit")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

PaymentMethodModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  size: PropTypes.oneOf(['default', 'large']),
  onExpand: PropTypes.func,
  methodKey: PropTypes.string,
  showFooter: PropTypes.bool,
  isEditMode: PropTypes.bool,
  alwaysEditable: PropTypes.bool,
  saving: PropTypes.bool,
  onEdit: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  textObj: PropTypes.object
};

export default PaymentMethodModal;
